// Home's Search Readiness widget (DC-50, DC-22; CR-095 "Search Readiness percent with missing prerequisites; posting is
// not blocked by pending Compensation."): per requisition the percent in a ring and its missing prerequisites as rows,
// each deep-linked to the screen that clears it. Posting is kept apart ("Readiness distinguishes posting from search
// preparedness."): S13 sends `posting_blocked: false` on every row, and the widget says so once in his words.

import { CheckRow, Checklist, Count, Paragraph, Ring, Row, RowName, Stack, Widget } from "@/mc";
import { METRIC_LABELS } from "@/mc/g3/labels";
import { SURFACE_LABELS } from "@/mc/core/labels";
import type { HomeWidgetView } from "./host";
import { DASH, figure, searchReadinessOf, seatsOf, type ReadinessRow, type SearchReadinessData } from "./api";
import { HOME_LABELS, HOME_TEMPLATES, PREREQUISITE_WORDS } from "./labels";
import { hsKey } from "@/views/b10/lib/hsKey";

export function ReadinessRowView({ row }: { row: ReadinessRow }) {
  const named = row.missing.filter((item) => PREREQUISITE_WORDS[item.key]);
  const others = row.missing.length - named.length;
  return (
    <Stack gap={4}>
      <Row cols={2} lead="70%" href={row.route || undefined} hs={hsKey("dash.readiness.req", row.requisition_id)}>
        <RowName name={row.title} sub={row.requisition_id} />
        {row.percent !== null
          ? <Ring size={44} pct={row.percent} label={HOME_TEMPLATES.searchReadinessPct({ pct: figure(row.percent, "percent") })} />
          : null}
      </Row>
      {row.missing.length > 0 ? (
        <Checklist>
          {named.map((item) => (
            <CheckRow key={item.key} status="todo" href={item.route || undefined} hs={hsKey("dash.readiness.missing", row.requisition_id, item.key)}>
              {PREREQUISITE_WORDS[item.key]}
            </CheckRow>
          ))}
          {others > 0 ? <CheckRow status="todo" count={others} href={row.route || undefined}>{SURFACE_LABELS.missingPrerequisites}</CheckRow> : null}
        </Checklist>
      ) : null}
    </Stack>
  );
}

export function SearchReadinessBody({ data }: { data: SearchReadinessData }) {
  const missing = data.rows.reduce((sum, row) => sum + row.missing.length, 0);
  return (
    <Stack gap={12}>
      {missing > 0 ? <Paragraph sm>{SURFACE_LABELS.missingPrerequisites}{" "}<Count n={missing} /></Paragraph> : null}
      {data.rows.map((row) => <ReadinessRowView key={row.requisition_id} row={row} />)}
      <Paragraph sm>{HOME_LABELS.postingNotBlocked}</Paragraph>
    </Stack>
  );
}

// D-25: drawn even with nothing in it, the Card's empty slot holding B10's DASH (as ActiveRequisitions.tsx says).
/** Widget `search_readiness` (S13 → S16, frozen). */
export const SearchReadiness: HomeWidgetView = ({ widgetKey, data }) => {
  const readiness = searchReadinessOf(data);
  const none = readiness.rows.length === 0;
  return (
    <Widget
      id={widgetKey}
      kind="health"
      roles={seatsOf(widgetKey)}
      title={METRIC_LABELS.searchReadiness}
      count={readiness.rows.length}
      empty={none ? DASH : undefined}
    >
      {none ? null : <SearchReadinessBody data={readiness} />}
    </Widget>
  );
};
